<template>
  <div v-if="geminiDraftTranslation || geminiLoading || geminiError" class="gemini-draft-container">
    <div class="gemini-draft-header" v-if="geminiLoading">
      <h3>
        <span v-if="geminiLoading" class="gemini-loading">
          <font-awesome-icon class="spinner-icon" icon="spinner"/> Generating...
        </span>
      </h3>
    </div>
    <div v-if="geminiError" class="gemini-error">
      <div v-if="geminiIsQuotaError">
        <div class="gemini-quota-help">
          <p>
            <strong>Daily AI quota reached.</strong> You can get your own free Gemini API key at
            <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer">Google AI Studio</a>
            and add it in <router-link to="/settings">Settings</router-link> to continue using AI translations.
          </p>
        </div>
      </div>
      <div v-else>
        <strong>Error:</strong> {{ geminiError }}
      </div>
    </div>
    <div v-if="geminiDraftTranslation" class="gemini-draft-content">
      <p>{{ geminiDraftTranslation }}</p>
      <div class="gemini-disclaimer">
        AI-generated translation. YMMV.
        <button @click="disableAiTranslation" class="btn-disable-ai">Disable AI Translation</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'GeminiDraftTranslation',
  props: {
    tibetanText: {
      type: [String]
    },
    results: {
      type: [Array, String, Object],
      default: null
    },
    enabled: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      geminiDraftTranslation: null,
      geminiLoading: false,
      geminiError: null,
      geminiIsQuotaError: false
    };
  },
  watch: {
    tibetanText: {
      handler(newVal) {
        if (newVal && this.enabled) {
          this.generateGeminiTranslation(newVal, this.results);
        }
      },
      immediate: false
    },
    enabled(newVal) {
      if (!newVal) {
        this.clearTranslation();
      }
    }
  },
  methods: {
    formatDictionaryEntriesForAI(results) {
      // Configuration constant for definition truncation (tweakable)
      const MAX_DEFINITION_LENGTH = 200;

      if (!results || results.length === 0) {
        return '';
      }

      const truncate = (text, maxLength) => {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
      };

      const addField = (label, value) => {
        if (!value || typeof value !== 'string' || value.trim() === '') return '';
        return `${label}: ${truncate(value, MAX_DEFINITION_LENGTH)}\n`;
      };

      let formattedEntries = 'DICTIONARY ENTRIES:\n\n';

      results.forEach((entry, index) => {
        formattedEntries += `ENTRY #${index + 1}:\n`;

        // Core identification fields
        formattedEntries += addField('Tibetan', entry.tibetan);
        formattedEntries += addField('Sanskrit', entry.sanskrit);
        formattedEntries += addField('Dictionary Source', entry.dictionary);

        // English definitions from various sources
        formattedEntries += addField('Hopkins 2015', entry.hopkins_2015);
        formattedEntries += addField('English Definition', entry.english1);
        formattedEntries += addField('English Definition 2', entry.english2);
        formattedEntries += addField('Other English', entry.english_others);
        formattedEntries += addField('Hackett', entry.hackett_definitions);
        formattedEntries += addField('Rangjung Yeshe', entry.rangjung_english);
        formattedEntries += addField('Berzin', entry.berzin);
        formattedEntries += addField('84000', entry.eightfourthousand_dict);

        // Tibetan definitions and explanations
        formattedEntries += addField('Tibetan Definition', entry.definition_tibetan_unicode);
        formattedEntries += addField('English Definition (detailed)', entry.definition_english);

        // Additional context
        formattedEntries += addField('Synonyms (Tibetan)', entry.synonyms_tibetan_unicode);
        formattedEntries += addField('Synonyms (English)', entry.synonyms_english);

        // Examples
        formattedEntries += addField('Example 1 (Tibetan)', entry.example_tibetan1_unicode);
        formattedEntries += addField('Example 1 (English)', entry.example_english1);
        formattedEntries += addField('Example 2 (Tibetan)', entry.example_tibetan2_unicode);
        formattedEntries += addField('Example 2 (English)', entry.example_english2);
        formattedEntries += addField('Example 3 (Tibetan)', entry.example_tibetan3_unicode);
        formattedEntries += addField('Example 3 (English)', entry.example_english3);
        formattedEntries += addField('Example 4 (Tibetan)', entry.example_tibetan4_unicode);
        formattedEntries += addField('Example 4 (English)', entry.example_english4);

        formattedEntries += '\n';
      });

      return formattedEntries;
    },
    async generateGeminiTranslation(tibetanText, results = null) {
      // Reset state
      this.geminiDraftTranslation = null;
      this.geminiError = null;
      this.geminiIsQuotaError = false;
      this.geminiLoading = true;

      try {
        let prompt;

        if (results && results.length > 0) {
          // Include dictionary context when results are provided
          const dictionaryContext = this.formatDictionaryEntriesForAI(results);
          prompt = `You are translating Classical Tibetan text. Below are dictionary entries for individual words/terms found in the text. Use these entries as reference to inform your translation, but produce a natural, fluent English translation of the complete text.

${dictionaryContext}

TEXT TO TRANSLATE:
${tibetanText}

Provide a natural, fluent English translation that captures the meaning. Only provide the translation, no explanation or additional commentary.`;
        } else {
          // Simple prompt when no dictionary context available
          prompt = `Translate the following Classical Tibetan text to English. Provide a natural, fluent translation that captures the meaning. Only provide the translation, no explanation or additional commentary:\n\n${tibetanText}`;
        }

        console.log("Gemini translation prompt:", prompt);

        const userApiKey = this.$store.state.app.userGeminiApiKey;

        const response = await axios.post(this.$server.apiUrl + '/api/gemini/query', {
          prompt: prompt,
          userApiKey: userApiKey,
          options: {
            temperature: 0.3,
            maxOutputTokens: 1000,
          }
        });

        if (response.data && response.data.text) {
          this.geminiDraftTranslation = response.data.text.trim();
        } else {
          this.geminiError = 'No translation received from AI service';
        }
      } catch (error) {
        this.$log.error('Gemini API error:', error);

        // Handle quota exceeded errors (429 status)
        if (error.response && error.response.status === 429) {
          const errorData = error.response.data;

          if (errorData && errorData.isQuotaError) {
            const retryAfter = errorData.retryAfter || 86400; // Default to 24 hours
            const retryTime = new Date(Date.now() + retryAfter * 1000);
            const hoursUntilReset = Math.ceil(retryAfter / 3600);

            this.geminiError = `Daily limit reached (20 requests). AI translation will be available again at ${retryTime.toLocaleTimeString()} (in ~${hoursUntilReset} hours).`;
            this.geminiIsQuotaError = true;
          } else {
            this.geminiError = 'Rate limit exceeded. Please try again in a moment.';
          }
        } else if (error.response && error.response.data && error.response.data.error) {
          this.geminiError = error.response.data.error;
        } else {
          this.geminiError = 'Failed to generate AI translation. Please try again.';
        }
      } finally {
        this.geminiLoading = false;
      }
    },
    disableAiTranslation() {
      // Disable the AI translation feature
      this.$store.commit('app/setEnableAiTranslation', false);

      // Clear the current translation display
      this.clearTranslation();

      // Show an alert to inform the user
      alert('AI-generated translations have been disabled. You can re-enable this feature in Settings.');
    },
    clearTranslation() {
      this.geminiDraftTranslation = null;
      this.geminiError = null;
      this.geminiIsQuotaError = false;
    },
    // Public method to trigger translation from parent
    translate(tibetanText, results = null) {
      this.generateGeminiTranslation(tibetanText, results);
    }
  }
};
</script>

<style scoped>
/* Gemini Draft Translation Styles */
.gemini-draft-container {
  background: linear-gradient(135deg, #95a5e8 0%, #f7efff 100%);
  color: white;
  border-radius: 12px;
  padding: 25px;
  margin: 20px auto;
  max-width: 900px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;
}

.gemini-draft-header h3 {
  margin: 0 0 15px 0;
  font-size: 1.5em;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
}

.gemini-loading {
  font-size: 0.7em;
  font-weight: 400;
  opacity: 0.9;
}

.gemini-draft-content {
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  border-radius: 8px;
  padding: 20px;
  line-height: 1.8;
}

.gemini-draft-content p {
  margin: 0 0 15px 0;
  font-size: 1.1em;
  text-align: left;
}

.gemini-disclaimer {
  font-size: 0.85em;
  font-style: italic;
  color: #666;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #ddd;
  text-align: center;
}

.btn-disable-ai {
  background: none;
  color: #dc3545;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: inherit;
  font-style: italic;
  text-decoration: underline;
  transition: color 0.2s;
}

.btn-disable-ai:hover {
  color: #a71d2a;
}

.gemini-error {
  background: rgba(255, 255, 255, 0.95);
  color: #d32f2f;
  border-radius: 8px;
  padding: 15px;
  font-weight: 500;
}

.gemini-quota-help {
  color: #555;
  font-weight: normal;
  font-size: 0.95em;
}

.gemini-quota-help p {
  margin: 0;
}

.gemini-quota-help a {
  color: #1976d2;
  text-decoration: underline;
}

.gemini-quota-help a:hover {
  color: #1565c0;
}

.spinner-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
